import React, { useCallback, useMemo } from "react"
import {
  Box,
  useTheme,
  Typography,
  Button,
  ToggleButtonGroup,
  CardMedia,
} from "@mui/material"
import { deepmerge } from "@mui/utils"
import { useNavigate, useParams } from "react-router-dom"
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined"

import {
  CURRENT_EVENT_FILTER_VALUE,
  Q_EVENT_GROUP,
  UPCOMING_EVENT_FILTER_VALUE,
} from "constant"

import { getIcon } from "utils/icon"
import { getDurationDate } from "utils/date"

import { useTranslation } from "providers/i18n"
import { useEventDetail } from "hooks/event/useEventDetail"
import { useEvent } from "hooks/event/useEvent"
import { useMobileUrl } from "hooks/useMobileUrl"

import { PageCard, PageCardContent } from "components/card"
import { NavigationControl } from "components/kioskHorizontal/navigation"
import { QRCode } from "components/QRCode"
import { MotionDiv } from "components/motions"
import {
  SkeletonEventDetailSidebar,
  VerticalEventCard,
} from "components/domains/events"
import { LocationIcon, PercentIcon } from "components/icons"
import { InfoListItem } from "components/ListItem"
import { Carousel, CarouselItem } from "components/Carousel"
import { EmptyData } from "components/EmptyData"
import { CategoryFilterChip } from "components/CategoryFilterBar"
import { ImageCarouselModal } from "components/Image"
import { size } from "lodash"
import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-h"
import { useImageModal } from "hooks/imageModal/useImageModal"

//TODO: remove this component when refactor setting initial active event group to a query string to reduce the number of re-renders.
const SidebarMenuContent = ({ AppNavigation = DefaultAppNavigation } = {}) => {
  const theme = useTheme()
  const { styleOverrides: bannerStyleOverrides = {} } =
    theme.components[`AppBanner`] || {}
  const sideBarStyleOverrides =
    theme.components[`AppSidebar`]?.styleOverrides || {}

  const { root: bannerRoot } = bannerStyleOverrides
  const { wrapper, body, footer } = sideBarStyleOverrides

  return (
    <MotionDiv
      sx={wrapper}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25 }}
    >
      <Box sx={bannerRoot}></Box>
      <Box sx={body}>{AppNavigation && <AppNavigation />}</Box>
      <Box sx={footer}>
        <NavigationControl />
      </Box>
    </MotionDiv>
  )
}

export const EventDetailKioskHPage = ({ Layout = Box }) => {
  const theme = useTheme()
  const navigate = useNavigate()

  const params = useParams()
  const { eventId } = params

  const { t } = useTranslation({
    defaultValue: null,
  })

  const { groupEvents, activeGroupEvent, handleChange } = useEvent()

  const { event, navigateToLocation } = useEventDetail(eventId)

  const { mobileUrl } = useMobileUrl()

  const { handleImage, openModal, activeImage, handleClose } = useImageModal()

  const eventToRender = groupEvents[activeGroupEvent]
  const hasEmptyList = event && eventToRender && eventToRender.length === 0
  const slidePerPage = 3
  const carouselType =
    eventToRender && eventToRender.length < slidePerPage ? "slide" : "loop"
  const label = `${activeGroupEvent}-event`

  const {
    locationButton,
    titleText,
    descriptionText,
    qrCode,
    detailContainer,
    infoListItem,
    locationButtonContainer,
    infoContainer,
    listItemContainer,
    qrcodeText,
    galleryCarousel,
    galleryCarouselItem,
    galleryCarouselImage,
    galleryContainer,
    sidebarContent,
    pageCard,
  } = theme.components[`EventDetailPage`]?.styleOverrides || {}

  const sideBarStyleOverrides =
    theme.components[`AppSidebar`]?.styleOverrides || {}
  const { wrapper, body, footer } = sideBarStyleOverrides

  const { styleOverrides: EventPageStyle } =
    theme.components?.[`EventPage`] || {}
  const { eventCarouselOption, galleryCarouselOption } =
    theme.components[`EventPage`]?.defaultProps || {}
  const {
    carouselContainer,
    carousel,
    eventCard,
    emptyDataWrapper,
    eventGroupSelectorButton,
  } = EventPageStyle

  const calendarIcon = useMemo(
    () => getIcon(theme?.assets?.calendarIcon) || <CalendarTodayOutlinedIcon />,
    [theme]
  )

  const locationIcon = useMemo(
    () => getIcon(theme?.assets?.locationIcon) || <LocationIcon />,
    [theme]
  )

  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.events) || <PercentIcon />,
    [theme]
  )

  const galleryUrls = useMemo(
    () => (size(event?.gallery) > 0 ? event?.gallery?.map((v) => v?.url) : []),
    [event?.gallery]
  )

  const handleOnReady = useCallback(
    (splide) => {
      const slideIndex = eventToRender.findIndex(
        (event) => event?.id === eventId
      )
      splide.go(slideIndex)
    },
    [eventToRender, eventId]
  )

  const handleClickSlide = useCallback((splide, { index, slide }, _event) => {
    const [activeSlideIndex] = slide.getAttribute("aria-label")?.split(" ")
    if (!activeSlideIndex) return
    splide.go(+activeSlideIndex - 1)
  }, [])

  const handleOnMoved = useCallback(
    (splide, index, prev, dest) => {
      const slides = splide?.root.querySelectorAll(
        ".splide__slide:not(.splide__slide--clone)"
      )
      const activeSlide = slides[index]
      const path = activeSlide?.getAttribute("link")
      navigate(path, {
        replace: true,
      })
    },
    [navigate]
  )

  const handleOnUpdated = useCallback(
    (splide) => {
      // Navigate to the active slide when switching event groups.
      const slides = splide?.root.querySelectorAll(
        ".splide__slide:not(.splide__slide--clone)"
      )
      const activeSlide = slides[splide?.index]
      const path = activeSlide?.getAttribute("link")

      if (path)
        navigate(path, {
          replace: true,
        })
    },
    [navigate]
  )

  if (!groupEvents) return null

  return (
    <>
      <Layout
        aside={
          <PageCard>
            {hasEmptyList ? (
              <SidebarMenuContent />
            ) : (
              <MotionDiv
                sx={deepmerge(wrapper, sidebarContent)}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.25 }}
              >
                <Box sx={body}>
                  {event ? (
                    <Box sx={detailContainer}>
                      <Typography variant="h6" sx={titleText}>
                        {t(event.name)}
                      </Typography>
                      {event.descriptionText && (
                        <Typography
                          sx={descriptionText}
                          variant="body1"
                          dangerouslySetInnerHTML={{
                            __html: event.descriptionText,
                          }}
                        />
                      )}
                      <Box sx={infoContainer}>
                        {mobileUrl && (
                          <Box>
                            <QRCode sx={qrCode} url={mobileUrl} />
                            <Typography
                              variant="subtitle2"
                              component="p"
                              sx={qrcodeText}
                            >
                              {t("Scan Here")}
                            </Typography>
                            <Typography
                              variant="subtitle2"
                              component="p"
                              sx={qrcodeText}
                            >
                              {t("For Mobile")}
                            </Typography>
                          </Box>
                        )}

                        <Box sx={listItemContainer}>
                          <InfoListItem
                            sx={infoListItem}
                            icon={calendarIcon}
                            primary={getDurationDate(
                              event.startDate,
                              event.endDate
                            )}
                            secondary={event.openingText}
                          />
                          {event.hasLocation && (
                            <InfoListItem
                              sx={infoListItem}
                              icon={locationIcon}
                              primary={t(event.location)}
                              secondary={t("Level Text", {
                                level: t(event.level),
                              })}
                            />
                          )}
                        </Box>
                      </Box>
                      {event.gallery && (
                        <Box sx={galleryContainer}>
                          <Carousel
                            options={{
                              type: "loop",
                              padding: 0,
                              gap: 8,
                              pagination: true,
                              autoplay: true,
                              ...galleryCarouselOption,
                            }}
                            sx={galleryCarousel}
                          >
                            {event.gallery.map(({ id, url }) => {
                              return (
                                <CarouselItem
                                  key={`slide-${id}`}
                                  sx={galleryCarouselItem}
                                >
                                  <CardMedia
                                    component="img"
                                    sx={galleryCarouselImage}
                                    src={url}
                                    onClick={() => handleImage(url)}
                                  />
                                </CarouselItem>
                              )
                            })}
                          </Carousel>
                        </Box>
                      )}
                      <Box sx={locationButtonContainer}>
                        {event.hasLocation && (
                          <Button
                            sx={locationButton}
                            variant="contained"
                            onClick={navigateToLocation}
                          >
                            {t("View Location")}
                          </Button>
                        )}
                      </Box>
                    </Box>
                  ) : (
                    <SkeletonEventDetailSidebar />
                  )}
                </Box>
                <Box sx={footer}>
                  <NavigationControl />
                </Box>
              </MotionDiv>
            )}
          </PageCard>
        }
      >
        <PageCard sx={pageCard}>
          <PageCardContent>
            <MotionDiv
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.25 }}
            >
              <Box sx={carouselContainer}>
                {groupEvents[activeGroupEvent] &&
                groupEvents[activeGroupEvent].length > 0 ? (
                  <Carousel
                    onClick={handleClickSlide}
                    options={{
                      type: carouselType,
                      pagination: true,
                      focus: "center",
                      gap: "0",
                      label,
                      ...eventCarouselOption,
                    }}
                    sx={carousel}
                    onUpdated={handleOnUpdated}
                    onReady={handleOnReady}
                    onMoved={handleOnMoved}
                  >
                    {groupEvents[activeGroupEvent].map((event) => {
                      const sponsoredId = event?.properties?.sponsoredId
                      return (
                        <CarouselItem
                          link={`/events/${event.id}?${Q_EVENT_GROUP}=${activeGroupEvent}`}
                          key={`${sponsoredId || ""}${event.id}`}
                        >
                          <VerticalEventCard event={event} sx={eventCard} />
                        </CarouselItem>
                      )
                    })}
                  </Carousel>
                ) : (
                  <Box sx={emptyDataWrapper}>
                    <EmptyData
                      icon={emptyIcon}
                      primary={
                        activeGroupEvent === CURRENT_EVENT_FILTER_VALUE
                          ? t(`No Current Events`)
                          : t(`No Upcoming Events`)
                      }
                      secondary={t("Check Back Soon")}
                    />
                  </Box>
                )}
              </Box>

              <Box sx={eventGroupSelectorButton}>
                <ToggleButtonGroup
                  value={activeGroupEvent}
                  onChange={handleChange}
                  exclusive
                >
                  <CategoryFilterChip value={CURRENT_EVENT_FILTER_VALUE}>
                    {t("Current Events")}
                  </CategoryFilterChip>
                  <CategoryFilterChip value={UPCOMING_EVENT_FILTER_VALUE}>
                    {t("Upcoming Events")}
                  </CategoryFilterChip>
                </ToggleButtonGroup>
              </Box>
            </MotionDiv>
          </PageCardContent>
        </PageCard>
      </Layout>
      <ImageCarouselModal
        activeSrc={activeImage}
        open={openModal}
        imageSrcs={galleryUrls}
        onClose={handleClose}
      />
    </>
  )
}
